import { Fragment, createElement } from 'react';
import { useFormContext } from 'react-hook-form';

import { Grid } from '@mui/material';

import {
  EmailsForm,
  FormCard,
  RenderFormField,
  SelectItem,
} from '@/components';
import { MultiAddressForm } from '@/components/CustomForms/AddressForm/MultiAddressForm';
import { SocialMediaForm } from '@/components/CustomForms/SocialMediaForm';
import { VerifiedBadge } from '@/components/VerifiedBadge';
import { citizenOfInput } from '@/constants/form_inputs';
import { useFormConfigLoader, useReligionOptions } from '@/hooks';
import { EmergencyContactsTable } from '@/layouts/TabLayout/MasterNames/components/tables/EmergencyContactsTable';
import { getItemDynamicOptions } from '@/pages/Kases/KaseCrimes/utilities';
import { handleReportClasses } from '@/pages/Kases/NIBRS/utils';

import { HeightExactField } from './HeightExactField';
import { LicenseInfoSection } from './LicenseInfoSection';
import { MasterNamePhoneNumberForm } from './MasterNamePhoneNumberForm';
import {
  IFieldItem,
  MasterNameFormFields,
  MasterNameIdentitySupplementalFields,
} from '../data';

type PersonInfoSectionKey =
  | 'identity'
  | 'personalInfo'
  | 'otherInfo'
  | 'emergencyContacts'
  | 'employerInfo'
  | 'physicalCharacteristics'
  | 'photos'
  | 'verified';

type MasterNamePersonInformationSectionProps = {
  isMasterIndex?: boolean;
  searchFieldKey?: string | null;
  showLicenseInfo?: boolean;
  showVerifiedBadges?: boolean;
  disabled?: boolean;
  sectionKeys?: PersonInfoSectionKey[];
  photosFieldName?: string;
  photosReadOnly?: boolean;
  verifiedFieldName?: string;
  getFullSSNApi?: () => Promise<any>;
  isFieldVerified?: (fieldName: string) => boolean;
  fieldNameMap?: Record<string, string>;
};

const specialFields: Record<string, React.ComponentType<any>> = {
  phoneNumbers: MasterNamePhoneNumberForm,
  socialMedia: SocialMediaForm,
  addresses: MultiAddressForm,
  emails: EmailsForm,
  'physicalCharacteristics.heightExact': HeightExactField,
};

const sectionsWithVerifiedBadges = ['identity', 'physicalCharacteristics'];

const insertIdentityField = (
  fields: IFieldItem[],
  field: IFieldItem,
  insertAfter?: string,
) => {
  if (fields.some((item) => item.name === field.name)) {
    return fields;
  }

  const nextFields = [...fields];
  const insertIndex = insertAfter
    ? nextFields.findIndex((item) => item.name === insertAfter)
    : -1;

  if (insertIndex === -1) {
    nextFields.push(field);
  } else {
    nextFields.splice(insertIndex + 1, 0, field);
  }

  return nextFields;
};

export const MasterNamePersonInformationSection = ({
  isMasterIndex,
  searchFieldKey,
  showLicenseInfo = true,
  showVerifiedBadges = true,
  disabled = false,
  sectionKeys = [
    'identity',
    'personalInfo',
    'otherInfo',
    'emergencyContacts',
    'employerInfo',
    'physicalCharacteristics',
    'photos',
    'verified',
  ],
  photosFieldName = 'files',
  photosReadOnly = false,
  verifiedFieldName = 'isVerified',
  getFullSSNApi,
  isFieldVerified,
  fieldNameMap,
}: MasterNamePersonInformationSectionProps) => {
  const { register, watch } = useFormContext();
  const formValues = watch();
  const emergencyContactsList = watch('emergencyContacts');

  const isFieldItem = (item: unknown): item is IFieldItem => {
    return !(!item || typeof item !== 'object');
  };

  const { config: userIdentityInputs, filteredOptions: filteredUserOptions } =
    useFormConfigLoader({
      formName: 'USER_INDENTITY',
      optionsName: 'USER',
      category: 'USER',
    });

  const { items: religionItems, addOption: addReligion } = useReligionOptions();

  const filteredSections = MasterNameFormFields.filter((section) =>
    sectionKeys.includes(section.key as PersonInfoSectionKey),
  );

  const combinedInputs = filteredSections.map((section) => {
    if (section.key !== 'identity') {
      return section;
    }

    let identityItems: IFieldItem[] = (
      userIdentityInputs ?? section.items
    ).filter(isFieldItem);

    for (const field of MasterNameIdentitySupplementalFields) {
      identityItems = insertIdentityField(
        identityItems,
        field,
        field.name === 'nysid' ? 'ssn' : 'nysid',
      );
    }

    return {
      ...section,
      items: identityItems,
    };
  });

  return (
    <>
      {combinedInputs.map(({ key, title, items: sectionItems }) => {
        if (key === 'emergencyContacts') {
          return (
            <FormCard
              className="grey-title"
              key={key}
              title={title}
              style={{ fontWeight: 500, marginBottom: '10px' }}
            >
              <Grid xs={12} item>
                <EmergencyContactsTable
                  emergencyContacts={
                    emergencyContactsList?.filter(
                      (ec: any) => !!ec?.emergencyContact,
                    ) ?? []
                  }
                  disabled={disabled}
                />
              </Grid>
            </FormCard>
          );
        }

        const renderLicenseInfoAfter = key === 'identity' && showLicenseInfo;

        return (
          <Fragment key={key}>
            {renderLicenseInfoAfter && (
              <LicenseInfoSection disabled={disabled} />
            )}
            <FormCard
              className="grey-title"
              key={key}
              title={title}
              style={{ fontWeight: 500 }}
            >
              {sectionItems.map(({ grid, ...rest }: any, index: number) => {
                const resolvedName =
                  fieldNameMap?.[rest.name] ??
                  (rest.name === 'files'
                    ? photosFieldName
                    : rest.name === 'isVerified'
                    ? verifiedFieldName
                    : rest.name);
                let inputItems: SelectItem[] = [];
                let itemRules;

                let processedFilteredUserOptions = filteredUserOptions;
                if (rest.name === 'race' && filteredUserOptions) {
                  processedFilteredUserOptions = filteredUserOptions.map(
                    (opt) => {
                      if (opt.type === 'race') {
                        return { ...opt, populate_rules: [] };
                      }
                      return opt;
                    },
                  );
                }

                if (
                  (rest.name === 'sex' || rest.name === 'ethnicOrigin') &&
                  filteredUserOptions
                ) {
                  processedFilteredUserOptions = filteredUserOptions.map(
                    (opt) => {
                      if (opt.type === rest.name) {
                        if (opt.items instanceof Map) {
                          const filteredEntries = (
                            Array.from(opt.items.entries()) as [string, any][]
                          ).filter(
                            ([, item]) => item.label !== 'Not Applicable',
                          );

                          return {
                            ...opt,
                            populate_rules: [],
                            items: new Map(filteredEntries),
                          };
                        }

                        return { ...opt, populate_rules: [] };
                      }
                      return opt;
                    },
                  );
                }

                const currentItemOptions = getItemDynamicOptions<any>(
                  rest.name,
                  processedFilteredUserOptions,
                  formValues,
                );

                if (currentItemOptions) {
                  const { items, rules } = currentItemOptions;
                  itemRules = rules;
                  inputItems = items;
                } else {
                  inputItems = rest.items;
                }

                if (rest.name === 'citizenOf') {
                  inputItems = citizenOfInput.items;
                }

                if (rest.name === 'religion') {
                  inputItems = religionItems;
                }

                return rest.type === 'hidden' ? (
                  <input
                    key={`${key}-${index}`}
                    type="hidden"
                    {...register(resolvedName)}
                  />
                ) : (
                  <Grid
                    key={`${key}-${index}`}
                    xs={12}
                    sm={6}
                    md={3}
                    {...grid}
                    {...rest}
                    name={resolvedName}
                    item
                    className={handleReportClasses(
                      resolvedName,
                      searchFieldKey ?? null,
                    )}
                  >
                    {specialFields[rest.name as string] ? (
                      createElement(specialFields[rest.name as string], {
                        ...rest,
                        disabled,
                        ...(rest.name === 'addresses' && isMasterIndex
                          ? {
                              showMunicipalityTypeInput: true,
                              isFieldVerified,
                            }
                          : {}),
                      })
                    ) : (
                      <div className="flex items-end w-full">
                        <div className="flex-1">
                          <RenderFormField
                            {...rest}
                            name={resolvedName}
                            disabled={disabled || rest.disabled}
                            {...(rest.name === 'files'
                              ? { readOnly: photosReadOnly }
                              : {})}
                            {...(resolvedName === 'ssn' && getFullSSNApi
                              ? { getFullSSNApi }
                              : {})}
                            {...((rest.type === 'autocomplete' ||
                              rest.type === 'select') && {
                              items: inputItems,
                            })}
                            {...(itemRules && {
                              rules: itemRules,
                            })}
                            {...(resolvedName === 'citizenOf' &&
                              !rest.value && {
                                defaultValue: '',
                              })}
                            {...(resolvedName === 'religion' && {
                              freeSolo: true,
                              addNew: true,
                              onCreate: addReligion,
                            })}
                          />
                        </div>
                        {showVerifiedBadges &&
                          isFieldVerified &&
                          sectionsWithVerifiedBadges.includes(key) &&
                          isFieldVerified(resolvedName) && (
                            <VerifiedBadge
                              tooltip="Verified by state ID"
                              className="mb-4"
                            />
                          )}
                      </div>
                    )}
                  </Grid>
                );
              })}
            </FormCard>
          </Fragment>
        );
      })}
    </>
  );
};

export default MasterNamePersonInformationSection;
